import { createSlice, createAsyncThunk, } from '@reduxjs/toolkit';
import { getApiErrorUserMessage } from '@pomofocus/core';
import { getApiClient } from '../../utils/apiClient';
import { todoListActions } from './todoListSlice';
import { paymentActions } from './paymentSlice';
// Async thunks
const fetchUser = createAsyncThunk('user/fetchUser', async (_, { rejectWithValue, dispatch }) => {
    try {
        const response = await getApiClient()({
            method: 'get',
            url: '/api/user',
        });
        // Trigger todo list fetch after successful user fetch
        dispatch(todoListActions.fetchTodoListAppId());
        return response.data;
    }
    catch (error) {
        const apiError = error;
        return rejectWithValue(apiError.message);
    }
});
const loginWithGoogle = createAsyncThunk('user/loginWithGoogle', async ({ code, todos, callback, }, { rejectWithValue }) => {
    try {
        const response = await getApiClient()({
            headers: {
                'X-Requested-With': 'XMLHttpRequest',
            },
            url: '/api/signup-with-google',
            method: 'post',
            data: {
                code,
                todos,
            },
        });
        // Fetch user data after successful Google login
        callback(response.data.token);
    }
    catch (error) {
        const apiError = error;
        return rejectWithValue(apiError.message);
    }
});
const loginWithEmail = createAsyncThunk('user/loginWithEmail', async ({ email, password, callback, }, { rejectWithValue }) => {
    try {
        const response = await getApiClient()({
            headers: {
                'X-Requested-With': 'XMLHttpRequest',
            },
            url: '/api/login-with-email',
            method: 'post',
            data: {
                email,
                password,
            },
        });
        // Fetch user data after successful email login
        callback(response.data.token);
    }
    catch (error) {
        const apiError = error;
        return rejectWithValue(getApiErrorUserMessage(apiError.errorCode));
    }
});
const signupWithEmail = createAsyncThunk('user/signupWithEmail', async ({ email, todos }, { rejectWithValue }) => {
    try {
        await getApiClient()({
            url: '/api/signup-with-email',
            method: 'post',
            data: { email, todos },
        });
        return true;
    }
    catch (error) {
        const apiError = error;
        return rejectWithValue(apiError.message);
    }
});
const loginWithAuthCode = createAsyncThunk('user/loginWithAuthCode', async ({ authCode, callback, }, { rejectWithValue }) => {
    try {
        const response = await getApiClient()({
            url: '/api/login-with-auth-code',
            method: 'post',
            data: { authCode },
        });
        callback(response.data.token);
    }
    catch (error) {
        const apiError = error;
        return rejectWithValue(apiError.message);
    }
});
const saveIdToken = createAsyncThunk('user/saveIdToken', async ({ code, authCode }, { rejectWithValue }) => {
    try {
        await getApiClient()({
            url: '/api/save-id-token',
            method: 'post',
            data: { code, authCode },
        });
        return true;
    }
    catch (error) {
        const apiError = error;
        return rejectWithValue(apiError.message);
    }
});
const setPassword = createAsyncThunk('user/setPassword', async ({ activationKey, password, callback, }, { rejectWithValue }) => {
    try {
        await getApiClient()({
            url: '/api/set-password-with-key',
            method: 'post',
            data: { activationKey, password },
        });
        callback();
        return true;
    }
    catch (error) {
        const apiError = error;
        return rejectWithValue(apiError.message);
    }
});
const saveProfile = createAsyncThunk('user/saveProfile', async ({ name, base64 }, { rejectWithValue }) => {
    try {
        const response = await getApiClient()({
            url: '/api/user/update',
            method: 'post',
            data: { name, base64 },
        });
        return response.data;
    }
    catch (error) {
        const apiError = error;
        return rejectWithValue(apiError.message);
    }
});
const deleteAccount = createAsyncThunk('user/deleteAccount', async ({ callback }, { rejectWithValue }) => {
    try {
        await getApiClient()({
            url: '/api/user/delete',
            method: 'post',
        });
        callback();
    }
    catch (error) {
        const apiError = error;
        return rejectWithValue(apiError.message);
    }
});
const getStreakDays = createAsyncThunk('user/getStreakDays', async ({ dateDigit }, { rejectWithValue }) => {
    try {
        const response = await getApiClient()({
            url: '/api/getstreak',
            method: 'post',
            data: {
                dateDigit,
            },
        });
        return response.data.dayStreak;
    }
    catch (error) {
        const apiError = error;
        return rejectWithValue(apiError.message);
    }
});
const startProTrial = createAsyncThunk('user/startProTrial', async ({ callback }, { rejectWithValue }) => {
    try {
        const response = await getApiClient()({
            url: '/api/start-pro-trial',
            method: 'post',
        });
        callback(response.data.trialUntil);
        return response.data;
    }
    catch (error) {
        const apiError = error;
        return rejectWithValue(apiError.message);
    }
});
const changeEmailRequest = createAsyncThunk('user/changeEmailRequest', async ({ email, onSuccess, onError, }, { rejectWithValue }) => {
    try {
        await getApiClient()({
            url: '/api/change-email-request',
            method: 'post',
            data: { email },
        });
        onSuccess();
        return true;
    }
    catch (error) {
        const apiError = error;
        onError(apiError.message);
        return rejectWithValue(apiError.message);
    }
});
const changeEmail = createAsyncThunk('user/changeEmail', async ({ code, onSuccess, onError, }, { rejectWithValue }) => {
    try {
        const response = await getApiClient()({
            url: '/api/change-email',
            method: 'post',
            data: { code },
        });
        onSuccess();
        return response.data.email;
    }
    catch (error) {
        const apiError = error;
        onError(apiError.message);
        return rejectWithValue(apiError.message);
    }
});
const verifyActivationKey = createAsyncThunk('user/verifyActivationKey', async ({ key }, { rejectWithValue }) => {
    try {
        const response = await getApiClient()({
            url: '/api/verify-activation-key',
            method: 'post',
            data: { key },
        });
        return response.data;
    }
    catch (error) {
        const apiError = error;
        return rejectWithValue(apiError.message);
    }
});
// Initial state
const INITIAL_USER = {
    _id: undefined,
    email: undefined,
    name: undefined,
    picture: undefined,
    created: undefined,
    customerId: null,
    subscriptionId: null,
    cancelOnNextRenewal: null,
    subscriptionRenewalDate: null,
    purchaseId: null,
    purchasePlan: null,
    purchaseDate: null,
    purchaseAmount: null,
    trialUntil: null,
    bulkUntil: null,
};
const initialState = {
    authenticated: undefined,
    user: INITIAL_USER,
    signupError: '',
    signupEmailSucceed: false,
    keyVarified: undefined,
    updatingEmail: false,
    updateEmailSuccess: false,
    loading: false,
    error: null,
};
// Slice
const userSlice = createSlice({
    name: 'user',
    initialState,
    reducers: {
        // Clear error messages
        clearError: (state) => {
            state.error = null;
            state.signupError = '';
        },
        // Display signup error
        displaySignupError: (state, action) => {
            state.signupError = action.payload.errorMsg;
        },
        // Handle subscription stop (from PaymentActions)
        // stopSubscription: state => {
        //   state.user.cancelOnNextRenewal = true;
        // },
        // Reset signup state
        resetSignupState: (state) => {
            state.signupEmailSucceed = false;
            state.signupError = '';
            state.keyVarified = undefined;
        },
        // Reset email update state
        resetEmailUpdateState: (state) => {
            state.updatingEmail = false;
            state.updateEmailSuccess = false;
        },
    },
    extraReducers: (builder) => {
        // Fetch user
        builder
            .addCase(fetchUser.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
            .addCase(fetchUser.fulfilled, (state, action) => {
            state.loading = false;
            state.authenticated = true;
            state.user = action.payload;
        })
            .addCase(fetchUser.rejected, (state, action) => {
            state.loading = false;
            state.authenticated = false;
            state.error = action.payload;
        });
        // Signup with email
        builder
            .addCase(signupWithEmail.pending, (state) => {
            state.loading = true;
            state.signupEmailSucceed = false;
        })
            .addCase(signupWithEmail.fulfilled, (state) => {
            state.loading = false;
            state.signupEmailSucceed = true;
        })
            .addCase(signupWithEmail.rejected, (state, action) => {
            state.loading = false;
            state.signupError = action.payload;
        });
        // Login with email
        builder
            .addCase(loginWithEmail.pending, (state) => {
            state.loading = true;
            state.signupError = '';
        })
            .addCase(loginWithEmail.fulfilled, (state) => {
            state.loading = false;
        })
            .addCase(loginWithEmail.rejected, (state, action) => {
            state.loading = false;
            state.signupError = action.payload;
        });
        // Login with auth code
        builder
            .addCase(loginWithAuthCode.pending, (state) => {
            state.loading = true;
        })
            .addCase(loginWithAuthCode.fulfilled, (state) => {
            state.loading = false;
        })
            .addCase(loginWithAuthCode.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });
        // Save ID token
        builder
            .addCase(saveIdToken.pending, (state) => {
            state.loading = true;
        })
            .addCase(saveIdToken.fulfilled, (state) => {
            state.loading = false;
        })
            .addCase(saveIdToken.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });
        // Set password
        builder
            .addCase(setPassword.pending, (state) => {
            state.loading = true;
            state.signupError = '';
        })
            .addCase(setPassword.fulfilled, (state) => {
            state.loading = false;
        })
            .addCase(setPassword.rejected, (state, action) => {
            state.loading = false;
            state.signupError = action.payload;
        });
        // Save profile
        builder
            .addCase(saveProfile.pending, (state) => {
            state.loading = true;
        })
            .addCase(saveProfile.fulfilled, (state, action) => {
            state.loading = false;
            state.user.name = action.payload.name;
            state.user.picture = action.payload.picUrl
                ? `${action.payload.picUrl}?${new Date().getTime()}`
                : undefined;
        })
            .addCase(saveProfile.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });
        // Delete account
        builder
            .addCase(deleteAccount.pending, (state) => {
            state.loading = true;
        })
            .addCase(deleteAccount.fulfilled, (state) => {
            state.loading = false;
            // Reset to initial state after successful deletion
            state.authenticated = false;
            state.user = INITIAL_USER;
        })
            .addCase(deleteAccount.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });
        // Start pro trial
        builder
            .addCase(startProTrial.pending, (state) => {
            state.loading = true;
        })
            .addCase(startProTrial.fulfilled, (state, action) => {
            state.loading = false;
            state.user.trialUntil = action.payload.trialUntil;
            state.user.subscriptionId = action.payload.subscriptionId;
        })
            .addCase(startProTrial.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });
        // Change email request
        builder
            .addCase(changeEmailRequest.pending, (state) => {
            state.loading = true;
        })
            .addCase(changeEmailRequest.fulfilled, (state) => {
            state.loading = false;
        })
            .addCase(changeEmailRequest.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });
        // Change email
        builder
            .addCase(changeEmail.pending, (state) => {
            state.updatingEmail = true;
            state.updateEmailSuccess = false;
        })
            .addCase(changeEmail.fulfilled, (state, action) => {
            state.updatingEmail = false;
            state.updateEmailSuccess = true;
            state.user.email = action.payload;
        })
            .addCase(changeEmail.rejected, (state, action) => {
            state.updatingEmail = false;
            state.updateEmailSuccess = false;
            state.error = action.payload;
        });
        // Verify activation key
        builder
            .addCase(verifyActivationKey.pending, (state) => {
            state.loading = true;
        })
            .addCase(verifyActivationKey.fulfilled, (state) => {
            state.loading = false;
            state.keyVarified = true;
        })
            .addCase(verifyActivationKey.rejected, (state, action) => {
            state.loading = false;
            state.signupError = action.payload;
        });
        builder.addCase(paymentActions.stopSubscription.fulfilled, (state) => {
            state.user.cancelOnNextRenewal = true;
        });
    },
});
// Export individual actions
const { clearError, displaySignupError, 
// stopSubscription,
resetSignupState, resetEmailUpdateState, } = userSlice.actions;
// Selectors
const selectUserState = (state) => state.user;
const selectUser = (state) => state.user.user;
const selectIsAuthenticated = (state) => state.user.authenticated;
const selectUserLoading = (state) => state.user.loading;
const selectUserError = (state) => state.user.error;
const selectSignupError = (state) => state.user.signupError;
const selectSignupEmailSucceed = (state) => state.user.signupEmailSucceed;
const selectKeyVerified = (state) => state.user.keyVarified;
const selectUpdatingEmail = (state) => state.user.updatingEmail;
const selectUpdateEmailSuccess = (state) => state.user.updateEmailSuccess;
// Derived selectors for common user checks
const selectIsPremium = (state) => {
    return !!state.user.user.purchasePlan;
};
const selectIsTrialActive = (state) => {
    const { trialUntil } = state.user.user;
    return trialUntil ? new Date(trialUntil) > new Date() : false;
};
const selectHasActiveSubscription = (state) => {
    const { user } = state.user;
    return !!(user.subscriptionId && !user.cancelOnNextRenewal);
};
const selectUserDisplayName = (state) => {
    const { user } = state.user;
    return user.name || user.email || 'User';
};
const selectIsEmailVerified = (state) => {
    return state.user.authenticated === true && !!state.user.user.email;
};
// Grouped exports for better organization
export const userActions = {
    ...userSlice.actions,
    // Sync actions
    clearError,
    displaySignupError,
    // stopSubscription,
    resetSignupState,
    resetEmailUpdateState,
    // Async actions
    fetchUser,
    loginWithGoogle,
    loginWithEmail,
    signupWithEmail,
    loginWithAuthCode,
    saveIdToken,
    setPassword,
    saveProfile,
    deleteAccount,
    getStreakDays,
    startProTrial,
    changeEmailRequest,
    changeEmail,
    verifyActivationKey,
};
export const userSelectors = {
    selectUserState,
    selectUser,
    selectIsAuthenticated,
    selectUserLoading,
    selectUserError,
    selectSignupError,
    selectSignupEmailSucceed,
    selectKeyVerified,
    selectUpdatingEmail,
    selectUpdateEmailSuccess,
    selectIsPremium,
    selectIsTrialActive,
    selectHasActiveSubscription,
    selectUserDisplayName,
    selectIsEmailVerified,
};
// Export reducer
export default userSlice.reducer;
